'use client';

import React, { forwardRef } from 'react'
import Link from 'next/link'
import { ControllerRenderProps, FieldValues, RegisterOptions } from 'react-hook-form'

import { ExternalLink } from '@fsg/icons'
import { useRequestOnMount } from '@fsg/next-auth'

import { ENDPOINTS } from '@app/constants'
import { APIResponse, CampaignResponse, Option } from '@app/types'
import { routes } from '@app/utils'
import { formatCampaignOption } from '@app/utils/selectOptions'

import { SelectField, SelectFieldProps } from '../Form'

export interface CampaignLookupProps extends SelectFieldProps {
  name: string
  placeholder?: string
  label: string
  isMulti?: boolean
  rules?: Omit<RegisterOptions<any, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>
}

const defaultParams = new URLSearchParams()
defaultParams.append('filter', 'status:eq(ACTIVE)')
defaultParams.append('limit', '5000')
const defaultPath = `${ENDPOINTS.CAMPAIGNS}?${defaultParams.toString()}`

export const CampaignLookup = forwardRef<any, CampaignLookupProps>((props, ref) => {
  const { name, placeholder, label, isMulti = false, ...selectProps } = props
  const { data: optionsData, status: optionsStatus } = useRequestOnMount<APIResponse<CampaignResponse>>({ path: defaultPath })

  // TODO: Refactor Display so it's not nested in this component; however, this requires refactoring SelectField/Typeahead too.
  // Although it's only a display element and probably won't experience unexpected behavior due to state updates, it's still an anti-pattern that can blow up later.
  type DisplayProps = ControllerRenderProps<FieldValues>
  function Display(field: DisplayProps) {
    if (isMulti) {
      return (
        <div className="py-md text-sm font-regular">
          {field.value.map((option: Option) => {
            const href = routes.campaign.general(option.value)
            return (
              <Link key={option.value} href={href}>
                <div className="flex justify-between">
                  <span>{option.label}</span> <ExternalLink className="inline" />
                </div>
              </Link>
            )
          })}
        </div>
      )
    } else {
      const { label, value: contactId } = field.value
      const href = routes.campaign.general(contactId)

      return (
        <div className="py-xs text-sm font-regular w-full">
          <Link href={href}>
            <div className="flex justify-between">
              <span>{label}</span> <ExternalLink className="inline" />
            </div>
          </Link>
        </div>
      )
    }
  }
  const renderDisplayComponent = (displayProps: DisplayProps) => <Display {...displayProps} />

  const options = optionsStatus === 'success' ? optionsData.data.map(formatCampaignOption) : []

  return (
    <SelectField
      name={name}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No results for ${inputValue}` : `Start typing to select ${label}`)}
      isMulti={isMulti ?? false}
      label={label}
      placeholder={placeholder}
      isLoading={optionsStatus === 'loading'}
      options={options}
      renderDisplayComponent={renderDisplayComponent}
      {...selectProps}
    />
  )
})
CampaignLookup.displayName = 'CampaignLookup'